import { ApiError, api } from '@alpha/api-client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, Copy, ExternalLink, QrCode, Wallet } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  connectWallet,
  connectWalletConnect,
  disconnectWalletConnect,
  ensureBscNetwork,
  hasInjectedProvider,
  metaMaskDeepLink,
  sendLdsPayment,
  shortAddress,
  signMessage,
  trustWalletDeepLink,
  WalletConnectError,
} from '../lib/wallet';
import { TOKEN_LABEL } from '../lib/format';
import { Alert, Button, Card, FormGroup, Input, Modal, Select } from '../ui';

/** One row of the post-registration credentials screen: a value + Copy button. */
function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="text-muted small" style={{ marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          <Input readOnly value={value} onFocus={(e) => e.currentTarget.select()} />
        </div>
        <Button
          type="button"
          variant="outline-gold"
          onClick={copy}
          leftIcon={copied ? <Check size={16} /> : <Copy size={16} />}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  );
}

/**
 * Wallet-connect registration — the 2026-07-30 replacement for the
 * email+password register form, per the client's explicit requirement:
 * "registration will be through Dapp ... with 2500 LDS token with full
 * security and validation features and address duplicacy should also be
 * checked" / "no password is needed now".
 *
 * Flow: connect wallet -> sign a nonce (proves wallet ownership) -> submit,
 * with NO tx_hash — the backend scans recent BSC blocks itself for a
 * qualifying >= 2,500 LDS transfer from this wallet to the deposit address
 * (App\Services\Auth\WalletAuthService::registerViaDapp /
 * App\Services\Web3\LdsTransferVerifier::findRecentTransfer). 2026-07-31:
 * removed the manual "paste your transaction hash" step per the client's
 * explicit instruction — "there is no need for transaction hash during
 * registration as the process is automatic". If the transfer hasn't been
 * picked up yet (still confirming, or sent moments ago), the response comes
 * back 'pending' and the member can just press Register again to re-check —
 * the SAME pending request is reused server-side, never a duplicate.
 *
 * 2026-07-31: the LDS payment itself is now automatic too, per the client's
 * explicit (and annoyed) instruction that the DApp should trigger the
 * transfer itself rather than showing a "copy this address" box — "AS SOON
 * AS THE DAPP IS CONNECTED THE RECEIVER ADDRESS IS AUTOMATICALLY FED FROM
 * THE SYSTEM WITH ALL SECURITY AND VALIDATIONS AND CHAIN ID IS 56 AND THE
 * ACCEPTABLE TOKEN IS LDS BEP20 TOKEN". The single "Send LDS & Register"
 * button below calls sendLdsPayment() (lib/wallet.ts) to fire an ERC20
 * transfer through the connected wallet's own signer — the wallet shows its
 * native confirm prompt — and the resulting tx hash is then passed straight
 * into api.walletAuth.register() so the backend can verify via its fast
 * known-hash path instead of scanning for a match.
 */
/**
 * 2026-09-11: real incident — a member's wallet sent the actual on-chain
 * payment successfully, but the SEPARATE signature step right after it
 * (signMessage(), a second distinct wallet approval) never completed —
 * closed the wallet app, a page reload, a dropped WalletConnect session,
 * anything between the two steps. txHash lived only in React state, so it
 * vanished, and retrying re-ran sendLdsPayment() and sent a SECOND real
 * payment instead of resuming — confirmed on-chain: two 2,500-token
 * payments landed at the deposit address with zero completed registrations
 * for either. Persisting the tx hash to localStorage, keyed by wallet
 * address, closes this: a reload/reconnect on the SAME wallet restores it
 * and skips straight to the nonce/signature/register step, exactly like an
 * in-session retry already correctly does.
 */
const PENDING_TX_KEY_PREFIX = 'alphaone_pending_reg_tx_';

function pendingTxKey(address: string): string {
  return PENDING_TX_KEY_PREFIX + address.toLowerCase();
}

function loadPendingTx(address: string): string | null {
  try {
    return window.localStorage.getItem(pendingTxKey(address));
  } catch {
    return null; // private browsing / storage disabled — falls back to in-memory only, same as before this fix.
  }
}

function savePendingTx(address: string, hash: string): void {
  try {
    window.localStorage.setItem(pendingTxKey(address), hash);
  } catch {
    // Best-effort — losing persistence here just means the pre-2026-09-11 behavior.
  }
}

function clearPendingTx(address: string): void {
  try {
    window.localStorage.removeItem(pendingTxKey(address));
  } catch {
    // Nothing to clean up if storage was never written.
  }
}

export function Register() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [sponsorCode, setSponsorCode] = useState(params.get('ref') ?? '');
  const [leg, setLeg] = useState<'left' | 'right'>(() => {
    const fromUrl = params.get('leg');
    return fromUrl === 'left' || fromUrl === 'right' ? fromUrl : 'left';
  });
  const [address, setAddress] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ userCode: string; txHash: string | null } | null>(null);
  const [pendingNotice, setPendingNotice] = useState<string | null>(null);
  const [hasProvider, setHasProvider] = useState(() => hasInjectedProvider());
  // Set once sendLdsPayment() broadcasts the transfer, so a retry after a
  // 'pending' response re-checks registration with the SAME known tx hash
  // instead of sending the 2,500 LDS a second time.
  const [txHash, setTxHash] = useState<string | null>(null);

  // Some wallet in-app browsers inject `window.ethereum` a beat after the
  // page loads rather than synchronously. Re-check once so the deep-link
  // fallback below doesn't linger for a mobile wallet browser that just
  // needed a moment to announce itself.
  useEffect(() => {
    if (hasProvider) return;
    const recheck = () => setHasProvider(hasInjectedProvider());
    window.addEventListener('ethereum#initialized', recheck, { once: true });
    const timer = window.setTimeout(recheck, 500);
    return () => {
      window.removeEventListener('ethereum#initialized', recheck);
      window.clearTimeout(timer);
    };
  }, [hasProvider]);

  // WalletConnect sessions hold a live relay socket open — dispose it if the
  // member navigates away without finishing registration, so it doesn't linger.
  useEffect(() => {
    return () => {
      disconnectWalletConnect().catch(() => {});
    };
  }, []);

  const paymentInfo = useQuery({
    queryKey: ['wallet-registration-info'],
    queryFn: () => api.walletAuth.registrationInfo().then((r) => r.data),
  });

  const connect = useMutation({
    mutationFn: async (method: 'injected' | 'walletconnect') => {
      await ensureBscNetwork();
      return method === 'walletconnect' ? connectWalletConnect() : connectWallet();
    },
    onSuccess: (wallet) => {
      setAddress(wallet);
      setFormError(null);

      // Restore a payment already sent for this wallet in a PRIOR attempt
      // (before a reload/navigation wiped the in-memory state) — see the
      // 2026-09-11 incident note above savePendingTx(). Without this, a
      // reconnect here would fall through submit()'s `if (!hash)` branch
      // and send the payment a second time.
      const restored = loadPendingTx(wallet);
      if (restored) {
        setTxHash(restored);
        setPendingNotice(
          `Found a ${TOKEN_LABEL} payment already sent from this wallet. Fill in your details and press ` +
            `"Send ${TOKEN_LABEL} & Register" to complete registration — this will NOT send the payment again.`,
        );
      }
    },
    onError: (err) => setFormError(err instanceof WalletConnectError ? err.message : 'Could not connect wallet.'),
  });

  const submit = useMutation({
    mutationFn: async () => {
      if (!address) throw new WalletConnectError('Connect your wallet first.');
      if (!name.trim()) throw new WalletConnectError('Name is required.');
      if (!sponsorCode.trim()) throw new WalletConnectError('Sponsor ID is required.');
      if (!/^\d{4,8}$/.test(pin)) throw new WalletConnectError('Transaction PIN must be 4-8 digits.');
      if (pin !== pinConfirm) throw new WalletConnectError('Transaction PINs do not match.');

      // Send the LDS payment automatically on first attempt only — if a
      // prior attempt already broadcast the transfer and came back
      // 'pending', txHash is already set and we just re-verify with the
      // same hash rather than sending 2,500 LDS again.
      let hash = txHash;
      if (!hash) {
        if (!paymentInfo.data) {
          throw new WalletConnectError('Payment details are still loading. Please wait a moment and try again.');
        }
        hash = await sendLdsPayment(
          address,
          paymentInfo.data.token_address,
          paymentInfo.data.deposit_address,
          paymentInfo.data.required_lds,
        );
        setTxHash(hash);
        // Persist BEFORE the next step (the separate signature approval)
        // can fail/be interrupted — see the 2026-09-11 incident note above.
        savePendingTx(address, hash);
      }

      const { data: nonceData } = await api.walletAuth.nonce({ wallet_address: address, purpose: 'register' });
      const signature = await signMessage(address, nonceData.message);

      return api.walletAuth.register({
        wallet_address: address,
        nonce: nonceData.nonce,
        signature,
        tx_hash: hash,
        sponsor_code: sponsorCode.trim(),
        leg,
        name: name.trim(),
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
        transaction_pin: pin,
        transaction_pin_confirmation: pinConfirm,
      });
    },
    onSuccess: (res) => {
      setFormError(null);
      const { status, request, user } = res.data;

      if (status === 'confirmed' && user) {
        if (address) clearPendingTx(address);
        setSuccess({ userCode: user.user_code as string, txHash: request.tx_hash });
        return;
      }

      if (status === 'pending') {
        setPendingNotice(
          `Your ${TOKEN_LABEL} payment was sent and is still confirming on-chain. This can take a minute or two — wait ` +
            `briefly, then press "Send ${TOKEN_LABEL} & Register" again to re-check (this will NOT send the payment again).`,
        );
        return;
      }

      // A genuine rejection is a permanent on-chain fact (wrong amount/token/
      // reverted) — the same hash retrying can't change that, so it's safe
      // to drop here (unlike the interrupted-flow case, which never reaches
      // this success handler at all and so never clears the persisted hash).
      if (address) clearPendingTx(address);
      setTxHash(null);
      setFormError(request.reject_reason ?? 'This registration could not be verified on-chain.');
    },
    onError: (err) => {
      setPendingNotice(null);
      if (err instanceof WalletConnectError || err instanceof ApiError) {
        setFormError(err.message);
      } else if (txHash) {
        // The payment step (setTxHash + savePendingTx) already completed
        // before whatever failed here (the signature step, most likely) —
        // reassure the member their payment is safe rather than let a
        // generic message make them think it wasn't and re-send it.
        setFormError(
          `Your ${TOKEN_LABEL} payment already went through — nothing was lost. Something interrupted the next ` +
            `step; press "Send ${TOKEN_LABEL} & Register" again to finish (this will NOT send the payment again).`,
        );
      } else {
        setFormError('Something went wrong while registering. Please try again.');
      }
    },
  });

  const closeSuccess = () => {
    setSuccess(null);
    navigate('/login', { replace: true });
  };

  return (
    <div className="auth-wrap">
      <Card className="auth-card">
        <h1 className="auth-title">Join Alpha One</h1>
        <p className="auth-subtitle">Register with your wallet — no password required.</p>

        {formError ? <Alert tone="danger">{formError}</Alert> : null}
        {pendingNotice ? <Alert tone="warning">{pendingNotice}</Alert> : null}

        {!hasProvider ? (
          <div style={{ marginBottom: 16 }}>
            <Alert tone="warning">No wallet app detected in this browser.</Alert>
            <p className="text-muted small" style={{ marginBottom: 8 }}>
              Continue in your wallet app&apos;s built-in browser instead — your sponsor ID link will carry over:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Button
                type="button"
                variant="outline-gold"
                className="w-100"
                rightIcon={<ExternalLink size={14} />}
                onClick={() => {
                  window.location.href = metaMaskDeepLink(window.location.href);
                }}
              >
                Open in MetaMask
              </Button>
              <Button
                type="button"
                variant="outline-gold"
                className="w-100"
                rightIcon={<ExternalLink size={14} />}
                onClick={() => {
                  window.location.href = trustWalletDeepLink(window.location.href);
                }}
              >
                Open in Trust Wallet
              </Button>
            </div>
          </div>
        ) : null}

        <FormGroup label="Name" required>
          <Input placeholder="Full Name" value={name} onChange={(e) => setName(e.target.value)} />
        </FormGroup>

        <FormGroup label="Sponsor ID">
          <Input placeholder="AO123456" value={sponsorCode} onChange={(e) => setSponsorCode(e.target.value)} />
        </FormGroup>

        <FormGroup label="Placement Leg">
          <Select
            value={leg}
            onChange={(e) => {
              const value = e.target.value as 'left' | 'right';
              setLeg(value);
              setParams(
                (prev) => {
                  const next = new URLSearchParams(prev);
                  next.set('leg', value);
                  return next;
                },
                { replace: true },
              );
            }}
          >
            <option value="left">Left</option>
            <option value="right">Right</option>
          </Select>
        </FormGroup>

        <FormGroup label="Mobile Number">
          <Input
            type="tel"
            placeholder="+91 98765 43210"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </FormGroup>

        <FormGroup label="Email">
          <Input
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </FormGroup>

        <FormGroup label="Transaction PIN" hint="4-8 digits — you'll use this to approve every withdrawal">
          <Input
            type="password"
            inputMode="numeric"
            placeholder="••••"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
          />
        </FormGroup>

        <FormGroup label="Confirm Transaction PIN">
          <Input
            type="password"
            inputMode="numeric"
            placeholder="••••"
            value={pinConfirm}
            onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, ''))}
          />
        </FormGroup>

        <div className="card" style={{ padding: 12, marginBottom: 16, background: 'var(--surface-2, #1a1a1a)' }}>
          <p className="text-muted small" style={{ marginBottom: 0 }}>
            You&apos;ll be asked to approve sending{' '}
            <strong>
              {paymentInfo.data?.required_lds ?? '2,500'} {TOKEN_LABEL}
            </strong>{' '}
            (
            {paymentInfo.data?.chain ?? 'BSC (BEP-20)'}) from your wallet — this happens automatically, no need to
            copy any address.
          </p>
        </div>

        {address ? (
          <p className="text-muted small" style={{ marginBottom: 12 }}>
            Wallet connected: <strong>{shortAddress(address)}</strong>
          </p>
        ) : (
          <div style={{ marginBottom: 16 }}>
            <Button
              type="button"
              variant="gold"
              className="w-100"
              leftIcon={<QrCode size={16} />}
              disabled={connect.isPending}
              onClick={() => connect.mutate('walletconnect')}
              style={{ marginBottom: 8 }}
            >
              {connect.isPending && connect.variables === 'walletconnect'
                ? 'Connecting…'
                : 'Connect with WalletConnect (any wallet)'}
            </Button>
            <Button
              type="button"
              variant="outline-gold"
              className="w-100"
              leftIcon={<Wallet size={16} />}
              disabled={connect.isPending}
              onClick={() => connect.mutate('injected')}
            >
              {connect.isPending && connect.variables === 'injected' ? 'Connecting…' : 'Connect Wallet'}
            </Button>
          </div>
        )}

        <Button
          type="button"
          variant="gold"
          className="w-100"
          disabled={!address || submit.isPending}
          onClick={() => submit.mutate()}
        >
          {submit.isPending
            ? txHash
              ? 'Verifying on-chain…'
              : 'Waiting for wallet…'
            : `Send ${paymentInfo.data?.required_lds ?? '2,500'} ${TOKEN_LABEL} & Register`}
        </Button>

        <p className="auth-footer">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </Card>

      <Modal
        open={success !== null}
        onClose={closeSuccess}
        title="Account Created"
        size="sm"
        footer={
          <Button type="button" variant="gold" onClick={closeSuccess} className="w-100">
            Continue to Sign In
          </Button>
        }
      >
        <p className="text-muted small" style={{ marginBottom: 16 }}>
          Your account has been verified on-chain and created. Save your User ID — you will sign in with your
          wallet from now on, there is no password.
        </p>
        {success ? <CopyRow label="Your User ID" value={success.userCode} /> : null}
        {success?.txHash ? <CopyRow label="Transaction Hash" value={success.txHash} /> : null}
      </Modal>
    </div>
  );
}
